import React, { useLayoutEffect } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { NavEmptyState } from '@atlassian/navigation-common/ui/nav-empty-state';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { getMessages } from '../../common/utils/get-messages';
import { getMessagesForLocation } from '../../common/utils/get-messages-for-location';
import type { NavErrorBoundaryProps } from '../types';

export function ErrorBoundaryUI({
	disableAnalytics,
	errorFallbackUI,
	errorActionUI,
	location,
	errorMessages,
	errorMessageValues,
}: ErrorBoundaryUIProps):
	| string
	| number
	| boolean
	| React.JSX.Element
	| Iterable<React.ReactNode>
	| null
	| undefined {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	// Message precedence: errorMessages (consumer-provided) > location-based lookup
	const { header, description, values } = fg('navx-4859-move-global-more-menu-messages')
		? getMessages({ errorMessages, errorMessageValues, location })
		: errorMessages
			? { header: errorMessages.header, description: errorMessages.description, values: undefined }
			: getMessagesForLocation(location);

	useLayoutEffect(() => {
		if (!disableAnalytics) {
			fireEvent('ui.errorBoundary.viewed.errorBoundaryUI', {
				location,
				isFallbackUI: !!errorFallbackUI,
			});
		}
	}, [disableAnalytics, errorFallbackUI, fireEvent, location]);

	// If a custom fallback UI is provided, render it directly at risk of consumer
	if (errorFallbackUI) {
		return errorFallbackUI({
			header: formatMessage(header, values),
			description: formatMessage(description, values),
		});
	}

	return (
		<NavEmptyState
			testId="navigation-error-boundary-ui"
			header={formatMessage(header, values)}
			description={formatMessage(description, values)}
			primaryAction={errorActionUI}
		/>
	);
}

type ErrorBoundaryUIProps = Pick<
	NavErrorBoundaryProps,
	'disableAnalytics' | 'errorFallbackUI' | 'errorActionUI' | 'errorMessages' | 'errorMessageValues'
> & { location: string };
